import type { FreightMatching } from "@x-mile/api-contract";
import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import type { Except } from "type-fest";

type TranslationResource = {
  shipper: string;
  carrier: string;
  size: string;
  accountRequest: Record<keyof FreightMatching.schemas.AccountRequest, string>;
  offerCargo: Record<
    keyof Except<FreightMatching.schemas.OfferCargo, "rejection">,
    string
  > & {
    desitination: Record<
      keyof FreightMatching.schemas.OfferCargo["destinations"][0],
      string
    >;
    rejection: Record<keyof FreightMatching.schemas.OfferRejection, string>;
  };
  deadlineType: Record<
    keyof typeof FreightMatching.schemas.OfferDestinationRequestBodyDeadlineType,
    string
  >;
  packingStyle: Record<
    keyof typeof FreightMatching.schemas.OfferCargoCollectionRequestBodyPackingStyle,
    string
  >;
  taskDetail: Record<
    keyof typeof FreightMatching.schemas.OfferDestinationTaskDetail,
    string
  >;
  vehicleShape: Record<
    keyof typeof FreightMatching.schemas.OfferCargoCollectionRequestBodyVehicleShape,
    string
  >;
  vehicleEquipment: Record<
    keyof typeof FreightMatching.schemas.MasterDataVehicleEquipmentsItem,
    string
  >;
};

declare module "i18next" {
  interface CustomTypeOptions {
    defaultNS: "translation";
    resources: {
      translation: TranslationResource;
    };
    keySeparator: ".";
  }
}

const resources: {
  ja: {
    translation: TranslationResource;
  };
} = {
  ja: {
    translation: {
      shipper: "荷主",
      carrier: "車主",
      size: "寸法",
      accountRequest: {
        id: "ID",
        address: "住所",
        companyCategory: "企業種別",
        companyName: "企業名",
        email: "メールアドレス",
        firstName: "名",
        firstNameKana: "名(カナ)",
        lastName: "姓",
        lastNameKana: "姓(カナ)",
        officeName: "事業所名",
        phoneNumber: "電話番号",
        zipCode: "郵便番号",
      },
      offerCargo: {
        id: "ID",
        name: "品名",
        company: "企業",
        office: "事業所",
        vehicle: "車両",
        sourceOfferCargoId: "",
        weight: "総重量",
        count: "数量",
        depth: "寸法(長さ)",
        width: "寸法(幅)",
        height: "寸法(高さ)",
        packingStyle: "荷姿",
        vehicleShape: "ボディタイプ",
        vehicleWeight: "トン数(車格)",
        vehicleEquipmentMemo:
          "車両の条件(重量、形状、冷房設備の有無、装備品など)",
        destinations: "配送ルート",
        loadingOn: "積日",
        unloadingOn: "卸日",
        deadlineAt: "提案受付期限",
        shippingFee: "運賃",
        highwayFee: "有料道路利用料の別途支払い",
        highwaySection: "有料道路区間",
        fuelSurcharge: "燃料サーチャージ",
        cargoHandlingFee: "荷役料",
        othersFee: "その他料金",
        adjustmentFee: "調整金額",
        paymentDate: "荷主からの入金予定日",
        basicFeeMemo: "備考(その他料金の内訳など)",
        memo: "特記事項",
        attachedDestinationFiles: "添付ファイル",
        attachedDeliveryRequestFiles: "配送依頼書",
        scheduledCarrier: "配送予定車両",
        process: "プロセス",
        stage: "ステージ",
        createdAt: "作成日",
        desitination: {
          id: "ID",
          hasLoading: "積込み",
          hasUnloading: "取卸し",
          prefecture: "都道府県",
          zipCode: "郵便番号",
          address: "住所",
          city: "市区町村",
          addressLine: "丁目・番地など",
          buildingName: "建物名",
          chargePersonName: "担当者名",
          contactInfo: "連絡先",
          deadlineType: "時間指定",
          deadlineTime: "時間",
          deadlineTimeEnd: "",
          taskDetail: "作業詳細",
          memo: "特記事項(日付等)",
        },
        rejection: {
          id: "ID",
          details: "詳細",
          reason: "見送り理由",
          offerCargosId: "オファーID",
        },
      },
      deadlineType: {
        till: "までに",
        after: "以降",
        just: "ちょうど",
        earliest: "最短",
        term: "時間帯指定",
      },
      packingStyle: {
        pallet: "パレット",
        case: "ケース",
        paper_band: "紙帯",
        freight_conveyor: "フレコン",
        none_package: "荷物そのまま",
        roll_box_pallet: "カゴ台車",
        container: "コンテナ",
        drum: "ドラム缶",
        plastic_container: "番重",
        other: "その他",
      },
      taskDetail: {
        crane: "クレーン",
        forklift_use_owner: "フォークリフト(自主荷役あり)",
        forklift_use_driver: "フォークリフト(自主荷役なし)",
        handlift: "ハンドリフト",
        unloading: "バラ",
        other: "その他",
      },
      vehicleShape: {
        other: "指定なし",
        flat: "平ボディ",
        box: "箱車",
        hood: "幌車",
        dump: "ダンプ",
        semi_trailer: "セミトレーラー",
        full_trailer: "フルトレーラー",
        wing: "ウィング",
        unic: "ユニック",
        refrigerator: "冷蔵車",
        freezer: "冷凍車",
      },
      vehicleEquipment: {
        air_suspension: "エアサス",
        roller: "ローラー",
        unic: "ユニック",
        food: "幌",
        wing: "ウィング",
        power_gate: "パワーゲート",
      },
    },
  },
};

i18n.use(initReactI18next).init({
  resources,
  lng: "ja",
});

export default i18n;
